import type { AllCMSPageFields } from '@/cms/fields/mappers/common'
import type {
  AppPageProps,
  RenderDocumentProps,
  RenderingStrategy,
  SlugPageProps,
} from '@/cms/rendering-strategy/types'
import { createConsoleLogger, PageComponent, Placeholder, UniformContextProvider } from '@uniformdev/next'
import * as React from 'react'
import { createIntl } from 'react-intl'
import { getComponentIndex as defaultGetComponentIndex } from '@/cms/getComponentIndex'
import { HybridPage } from '@/cms/rendering-strategy/HybridPage'
import { getFonts } from '@/framework/react-styles'
import StyledComponentsRegistry from '@/framework/styled-components/styled-components-registry'
import { getBrandName } from '@/helpers/environment'
import customIntlOnError from '@/intl/customIntlOnError'
import { getCatalogueTypeByPublicationCode } from '@/scopes/common/domain/functions/catalogues'

export function hybridRenderingStrategy(): RenderingStrategy {
  function renderDocument({ Head, Html, Main, NextScript, pageProps }: RenderDocumentProps) {
    // eslint-disable-next-line unused-imports/no-unused-vars -- Config available
    const { page, metadata, config } = pageProps
    const { name, template, fields } = page || {}
    const { bodyAttrs, htmlLang } = fields || {}
    const { machineName, staticFileName } = bodyAttrs || {}

    // TODO use brand name from config instead
    const brandName = getBrandName()
    const { primaryFont, secondaryFont } = getFonts(brandName)

    return (
      <Html lang={htmlLang}>
        <script
          // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: `
              window.sunwebgroup_legacy_design_system = {}
              window.sunwebgroup_legacy_design_system.REACT_REHYDRATION_MODE_ENABLED = true
            `,
          }}
        />
        {/* TODO review: are these metadata being used? */}
        <Head>
          {metadata?.title && <title>{metadata.title}</title>}
          {metadata?.faviconUrl && <link rel="shortcut icon" type="image/x-icon" href={metadata.faviconUrl} />}
        </Head>
        <body
          className={`o-block o-block--stretch l-site t-${staticFileName} ${primaryFont.variable} ${secondaryFont.variable}`}
          data-in-name={machineName}
          data-qa-page={name}
          data-qa-template={template}
        >
          <StyledComponentsRegistry>
            <Main />
            <NextScript />
          </StyledComponentsRegistry>
        </body>
      </Html>
    )
  }

  function renderApp({ Component, pageProps, ReactPage }: AppPageProps) {
    const locale = pageProps.config.locale
    const intl = createIntl({ locale, messages: pageProps.translations, onError: customIntlOnError })

    return (
      <ReactPage
        locale={locale}
        intl={intl}
        // TODO: This casting should be removed. In order to do so we need to remove exceptions for pages not using Uniform data
        cmsFields={pageProps?.cmsFields as AllCMSPageFields}
        config={pageProps.config}
        tokens={pageProps.tokens}
        abTest={{
          isEnabled: Boolean(pageProps.enableAbTest),
          isBVariant: Boolean(pageProps.isAbTestBVariant),
        }}
      >
        <HybridPage>
          <Component {...pageProps} />
        </HybridPage>
      </ReactPage>
    )
  }

  function renderPageComponent({
    reactComponentsList: _reactComponentsList,
    reactComponentParameters: _reactComponentParameters,
    getComponentIndex = defaultGetComponentIndex,
    ...pageProps
  }: SlugPageProps) {
    // TODO We're passing the url from uniform fields, but may be we should pass the url from the path
    const componentsIndex = getComponentIndex(
      getCatalogueTypeByPublicationCode(pageProps.config?.publicationCode),
      pageProps.page?.template,
    )

    return (
      <UniformContextProvider logger={createConsoleLogger()} componentMap={componentsIndex}>
        <PageComponent {...pageProps}>
          {renderingContext => <Placeholder placeholderKey="/" renderingContext={renderingContext} />}
        </PageComponent>
      </UniformContextProvider>
    )
  }

  return {
    type: 'hybrid',
    renderDocument,
    renderApp,
    renderPageComponent,
  }
}

export default HybridPage
